import { useContext, useState } from "react";
import { CarColors, ConcertTypes, Distance, Locations, ProfileContext } from "../contexts/AppContext";
import { AudioContext } from "../contexts/AudioContext";
import { AnimatePresence } from "motion/react";
import * as motion from "motion/react-client";
import { useForm } from "react-hook-form";
import { Filter } from "bad-words";

import "./screens.scss";
import "./landing.scss";

import { defaultSectionTransition } from "../transitions";
import { NavLink } from "react-router-dom";

export default function Landing() {
  const { setProfile, location, concertType } = useContext(ProfileContext);
  if (!setProfile) return null;

  const { setSrc, play, setVolume } = useContext(AudioContext);

  const enum View {
    WELCOME,
    REGISTRATION,
    INTRO_1,
    INTRO_2,
  }
  const [activeView, setActiveView] = useState(View.WELCOME);

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm();
  const filter = new Filter();

  const carColor = watch("Car Color", "white");

  const onSubmit = (data) => {
    let hasProfanity = false;

    if (filter.isProfane(data["First name"])) {
      setValue("First name", "");
      setError("First name", {
        type: "manual",
        message: "Inappropriate language. Fields were cleared.",
      });
      hasProfanity = true;
    } else {
      clearErrors("First name");
    }

    if (filter.isProfane(data["Last name"])) {
      setValue("Last name", "");
      setError("Last name", {
        type: "manual",
        message: "Inappropriate language. Fields were cleared.",
      });
      hasProfanity = true;
    } else {
      clearErrors("Last name");
    }

    if (!hasProfanity) {
      setProfile({
        firstName: data["First name"],
        lastName: data["Last name"],
        location: data["Start Location"],
        carColor: data["Car Color"],
        concertType: data["Concert type"],
        happiness: 0,
        time: 0,
        battery: 0,
        timeBonus: 0,
        currentChallenge: 0,
      });
      setActiveView(View.INTRO_1);

      const concert = data["Concert type"];
      setSrc?.(`audio/${concert.toLowerCase()}.mp3`);
      setVolume?.(0.1);
      play?.();
    }
  };

  return (
    <section className="screen screen--landing" style={{ backgroundImage: `url("bg-landing.jpg")` }}>
      <AnimatePresence mode={"popLayout"} initial>
        {activeView == View.WELCOME && (
          <motion.section
            key="landing__section--welcome"
            className="landing__section landing__section--welcome"
            {...defaultSectionTransition}
          >
            <img className="welcome__logo" src="lockup.svg" />
            <button
              className="button button--green"
              onClick={() => {
                setActiveView(View.REGISTRATION);
              }}
            >
              Play the Game
            </button>
                   <NavLink className="button button--black" to="/leaderboard">
                View Leaderboard
              </NavLink>
          </motion.section>
        )}
        {activeView == View.REGISTRATION && (
          <motion.section
            key="landing__section--registration"
            className="landing__section landing__section--registration"
            {...defaultSectionTransition}
          >
            <section className="landing__modal">
              <header className="landing__header">
                <img className="landing__image landing__image--0" src={`intro-0-${carColor?.toLowerCase()}.png`} />
                <h1>Register your vehicle</h1>
              </header>
              <section className="landing__body">
                <form id="registration" onSubmit={handleSubmit(onSubmit)}>
                  <fieldset className="error-container">
                  {errors["First name"] && ( <div className="error error-first">{String(errors["First name"].message)}</div>)}
                  {errors["Last name"] && <div className="error error-last">{String(errors["Last name"].message)}</div>}
                    <div>
                      <input
                        type="text"
                        placeholder="First name"
                        maxLength={12}
                        {...register("First name", { required: true })}
                      />
                      <input
                        type="text"
                        placeholder="Last name (first three letters)"
                        maxLength={3}
                        {...register("Last name", { required: false })}
                      />
                    </div>
                  </fieldset>
                  <fieldset>
                    <div className="two-col">
                      <div>
                        <legend>Start Location</legend>
                        <select {...register("Start Location", { required: true })}>
                          {Object.entries(Locations).map(([key, value]) => (
                            <option key={key} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <legend>Car Color</legend>
                        <select {...register("Car Color", { required: true })}>
                          {Object.entries(CarColors).map(([key, value]) => (
                            <option key={key} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </fieldset>
                  <fieldset>
                    <div>
                      <legend>Concert type</legend>
                      <div className="radio-group">
                        {Object.entries(ConcertTypes).map(([key, value]) => (
                          <label key={`label-${key}`} className="radio-option">
                            <input
                              key={`input-${key}`}
                              {...register("Concert type", { required: true })}
                              type="radio"
                              value={value}
                              className="radio-hidden"
                            />
                            <img src={`icon-${value.toLowerCase()}.png`} />
                            <span>{value}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </fieldset>
                </form>
              </section>
              <input
                type="submit"
                value="Submit"
                form="registration"
                className="button button--black"
                disabled={!isValid}
              />
            </section>
          </motion.section>
        )}
        {activeView == View.INTRO_1 && (
          <motion.section
            key="landing__section--intro"
            className="landing__section landing__section--intro"
            {...defaultSectionTransition}
          >
            <section className="landing__modal">
              <header className="landing__header">
                <img className="landing__image landing__image--1" src={`intro-${concertType?.toLowerCase()}.png`} />
                <h1>Congratulations!</h1>
              </header>
              <section className="landing__body">
                <p>
                  You and your family have just won tickets to a family music festival! The only catch is that the
                  festival is in San Francisco, California. It's time to break in your new EV on a{" "}
                  {Distance[location || "xxxxx"]}-mile road trip!
                </p>
                <p>
                  The festival is on a Friday, so you plan to leave your home in {location} at 6 AM, 7 days before the
                  festival, to make it there in one week. You have consulted your vehicle's navigation system and
                  planned your route, including all the charging stops.
                </p>
              </section>
              <button
                className="button button--black"
                onClick={() => {
                  setActiveView(View.INTRO_2);
                }}
              >
                Next
              </button>
            </section>
          </motion.section>
        )}
        {activeView == View.INTRO_2 && (
          <motion.section
            key="landing__section--intro-2"
            className="landing__section landing__section--intro"
            {...defaultSectionTransition}
          >
            <section className="landing__modal">
              <header className="landing__header">
                <img className="landing__image landing__image--2" src="intro-2.png" />
                <h1>Gameplay Instructions!</h1>
              </header>
              <section className="landing__body">
                <p>
                  Throughout your EV road trip, you will be greeted by multiple trip-related questions. Your score is
                  based on how you answer the questions, your reputation with your family, and how quickly you get to
                  the festival.
                </p>
                <p>Let's get started!</p>
              </section>
              <NavLink className="button button--black" to="/game">
                Begin
              </NavLink>
            </section>
          </motion.section>
        )}
      </AnimatePresence>
    </section>
  );
}
